// src/components/DrawingIntake.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SketchCanvas from './SketchCanvas.jsx';
import { floorLabel } from './DesignReferencesCard';
import { setPreferredPlan } from '../utils/designStore';
import { pixelArea, scaleFromLine, siteFromDrawing } from '../utils/drawingSite';

/**
 * Sketch, photo or PDF — started as a NEW project.
 *
 * Draw or choose the drawing, then Let's Design. That uploads it and traces its
 * outline, and opens the Save Project window, which asks for the three facts a
 * drawing cannot supply by itself: where the site is, which way is north, and
 * the real length of one line. From those the traced outline becomes a site
 * boundary in latitude and longitude, and the project is saved exactly as a
 * map-traced one is. The drawing is attached as its ground floor plan, with the
 * scale already set, so Design Analysis opens on it ready to read.
 */

const MAX_MB = 20;   // the ceiling the server store enforces
const FT = 0.3048;

const COPY = {
  sketch: {
    title: 'Sketch your site or plan',
    sub: 'Draw by hand with a mouse, a pen or a finger — a closed outer boundary with straight lines traces best. When it is ready, press Let’s Design to save it as a new project.',
  },
  photo: {
    title: 'Upload a photo',
    sub: 'A photograph or screenshot of a site plan or floor plan. Press Let’s Design to save it as a new project.',
  },
  pdf: {
    title: 'Upload a PDF',
    sub: 'A site plan or floor plan as a PDF. Press Let’s Design to save it as a new project.',
  },
};

const stamp = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
const mb = (bytes) => `${(bytes / 1048576).toFixed(1)} MB`;
const fmt = (n, dp = 1) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: dp, minimumFractionDigits: dp });

/** Read a JSON reply, turning an empty or non-JSON body into a sentence. */
async function readJson(pending, what) {
  const res = await pending;
  const raw = await res.text();
  let d = null;
  try { d = JSON.parse(raw); } catch { d = null; }
  if (res.ok && d?.ok !== false) return d || {};
  if (res.status === 401) throw new Error('Your session has ended. Sign in again, then save.');
  throw new Error(d?.error || d?.message || (raw.trim()
    ? `${what} failed (HTTP ${res.status}).`
    : 'No reply from the VAUOM server — it is probably not running.'));
}

/**
 * Decode any image the browser can read and re-encode it as JPEG, capped in size.
 * Only for photos that are not already JPEG or PNG (HEIC, WebP) or are too big —
 * a PNG screenshot goes untouched, because JPEG artefacts along thin lines are
 * exactly what line detection trips on.
 */
async function toJpeg(file, maxEdge = 4096) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('unreadable'));
      i.src = url;
    });
    const k = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth * k));
    c.height = Math.max(1, Math.round(img.naturalHeight * k));
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';   // a transparent image would otherwise turn black in JPEG
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return await new Promise((resolve, reject) => {
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('unreadable'))), 'image/jpeg', 0.92);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Where north points on the drawing, as a small compass laid on the image. */
function NorthBadge({ deg }) {
  return (
    <span className="intake-north" title={`North is ${Math.round(deg)}° clockwise from the top of the drawing`}>
      <svg viewBox="0 0 24 24" aria-hidden="true" style={{ transform: `rotate(${deg}deg)` }}>
        <path d="M12 3 16 13H8z" fill="currentColor" />
        <path d="M12 21 8 13h8z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      <span className="intake-north-n">N</span>
    </span>
  );
}

const QUICK_NORTH = [
  { deg: 0, label: '↑ Top' }, { deg: 90, label: '→ Right' },
  { deg: 180, label: '↓ Bottom' }, { deg: 270, label: '← Left' },
];

/* ---- the Save Project window ------------------------------------------------ */
function SaveProjectDialog({ draft, kind, apiBase, onCancel, onCreateProject, onSaved }) {
  const dialogRef = useRef(null);
  const svgRef = useRef(null);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [place, setPlace] = useState(null);           // { lat, lng, label }
  const [placeMsg, setPlaceMsg] = useState('');
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);                     // { q, run } — one lookup per typed place
  const [north, setNorth] = useState('');
  const [points, setPoints] = useState([]);
  const [length, setLength] = useState('');
  const [unit, setUnit] = useState('m');
  // Two ways to set the scale: mark a line of known length, or state how wide the
  // traced outline really is — often the plot width the owner already knows.
  const [method, setMethod] = useState('line');     // 'line' | 'width'
  const [widthLen, setWidthLen] = useState('');
  // Keyboard picking: a crosshair moved with the arrow keys, set with Enter.
  const [cursor, setCursor] = useState(null);       // [x, y] in parse pixels
  const [pickMsg, setPickMsg] = useState('');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) { try { d.showModal(); } catch { d.setAttribute('open', ''); } }
  }, []);

  const { w, h } = draft.parse.imageSize;
  // The traced outline, correctable by hand. The tracer follows the ink, so a door
  // swing drawn past a wall, or a corner that does not quite close, bends it; the
  // corners can be dragged, added and removed. What is saved is THIS outline.
  const traced = draft.parse.outline;
  const [outline, setOutline] = useState(() => traced.map((q) => [q[0], q[1]]));
  const edited = useMemo(() => JSON.stringify(outline) !== JSON.stringify(traced), [outline, traced]);
  const dragRef = useRef(null);          // { idx, x, y, id, moved, added } while a handle is held
  const handledRef = useRef(false);      // the click that ends a handle press is not a line pick
  const vertexRefs = useRef([]);
  const [focusIdx, setFocusIdx] = useState(0);
  const toM = (v) => (unit === 'ft' ? Number(v) * FT : Number(v));
  const lengthM = toM(length);
  const widthM = toM(widthLen);
  const bounds = useMemo(() => {
    const xs = outline.map((p) => p[0]);
    const ys = outline.map((p) => p[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    return { minX, maxX, maxY: Math.max(...ys), widthPx: maxX - minX };
  }, [outline]);
  const mpp = method === 'width'
    ? (bounds.widthPx > 0 && widthM > 0 ? widthM / bounds.widthPx : null)
    : (points.length === 2 ? scaleFromLine(points[0], points[1], lengthM) : null);
  const northDeg = north === '' || !Number.isFinite(Number(north)) ? null : ((Number(north) % 360) + 360) % 360;

  // What the site will measure, before anything is saved — the check that the
  // line and its length were entered the right way round.
  const preview = useMemo(() => {
    if (!mpp) return null;
    const xs = outline.map((p) => p[0]);
    const ys = outline.map((p) => p[1]);
    return {
      areaM2: pixelArea(outline) * mpp * mpp,
      widthM: (Math.max(...xs) - Math.min(...xs)) * mpp,
      depthM: (Math.max(...ys) - Math.min(...ys)) * mpp,
    };
  }, [mpp, outline]);

  // A click on the drawing, in the PARSE's pixel space: the SVG's viewBox is that
  // space, so its screen transform converts the pointer directly — the same space
  // the outline and the rooms are measured in.
  const toSvg = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg || !svg.getScreenCTM) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return [p.x, p.y];
  };
  const pick = (e) => {
    if (handledRef.current) { handledRef.current = false; return; }
    const p = toSvg(e.clientX, e.clientY);
    if (p) setPoint(p);
  };

  /* ---- editing the outline ---- */
  const clampPt = ([x, y]) => [Math.round(Math.max(0, Math.min(w, x))), Math.round(Math.max(0, Math.min(h, y)))];
  const moveVertex = (i, q) => setOutline((cur) => cur.map((v, j) => (j === i ? clampPt(q) : v)));
  const insertAfter = (i) => {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    setOutline((cur) => [...cur.slice(0, i + 1), clampPt([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]), ...cur.slice(i + 1)]);
  };
  const removeVertex = (i) => {
    if (outline.length <= 3) { setPickMsg('An outline needs at least three corners.'); return; }
    setOutline((cur) => cur.filter((_, j) => j !== i));
    setFocusIdx((f) => Math.min(f, outline.length - 2));
    setPickMsg(`Corner ${i + 1} removed. The outline has ${outline.length - 1} corners.`);
  };

  // Press on a corner or an edge's midpoint. The midpoint adds a corner there and
  // drags it; a corner is dragged once the pointer has moved a few pixels.
  const onHandleDown = (e, i, isMid) => {
    if (e.button != null && e.button !== 0) return;
    e.stopPropagation();
    handledRef.current = true;
    if (isMid) insertAfter(i);
    dragRef.current = { idx: isMid ? i + 1 : i, x: e.clientX, y: e.clientY, id: e.pointerId, moved: false, added: isMid };
    try { svgRef.current.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    if (!isMid) setFocusIdx(i);
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 4) return;
    d.moved = true;
    const q = toSvg(e.clientX, e.clientY);
    if (q) moveVertex(d.idx, q);
  };
  const onDragEnd = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    dragRef.current = null;
    if (d.added) { setPickMsg(`Corner added — the outline has ${outline.length} corners.`); return; }
    if (d.moved) { setPickMsg(`Corner ${d.idx + 1} moved.`); return; }
    // A corner pressed and released without moving marks a line end exactly on it —
    // where a plot edge's length is measured.
    if (method === 'line' && e.type === 'pointerup') setPoint(outline[d.idx]);
  };

  // Keyboard, on a focused corner: arrows move it (Shift for bigger steps),
  // Page Up / Page Down go to the previous / next corner, Insert adds one after
  // it, Delete removes it, Enter marks it as a line end.
  const onHandleKey = (e, i) => {
    const step = (e.shiftKey ? 0.02 : 0.004) * Math.max(w, h);
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const go = (j) => { const n = (j + outline.length) % outline.length; setFocusIdx(n); vertexRefs.current[n]?.focus(); };
    let done = true;
    if (moves[e.key]) moveVertex(i, [outline[i][0] + moves[e.key][0], outline[i][1] + moves[e.key][1]]);
    else if (e.key === 'PageDown') go(i + 1);
    else if (e.key === 'PageUp') go(i - 1);
    else if (e.key === 'Insert' || e.key === '+') { insertAfter(i); setPickMsg(`Corner added after corner ${i + 1}.`); }
    else if (e.key === 'Delete' || e.key === 'Backspace') removeVertex(i);
    else if ((e.key === 'Enter' || e.key === ' ') && method === 'line') setPoint(outline[i]);
    else done = false;
    if (done) { e.preventDefault(); e.stopPropagation(); }
  };
  const resetOutline = () => {
    setOutline(traced.map((q) => [q[0], q[1]]));
    setFocusIdx(0);
    setPickMsg('Outline reset to the traced one.');
  };

  // Positions are read out as a share of the drawing: pixels mean nothing to a
  // listener, but "a quarter of the way across" does.
  const where = ([x, y]) => `${Math.round((x / w) * 100)}% across, ${Math.round((y / h) * 100)}% down`;
  const setPoint = ([x, y]) => {
    const q = [Math.max(0, Math.min(w, x)), Math.max(0, Math.min(h, y))];
    const next = points.length >= 2 ? [q] : [...points, q];
    setPoints(next);
    setPickMsg(next.length === 1
      ? `First end set, ${where(q)}. Now set the other end.`
      : `Second end set, ${where(q)}. Enter the line's real length.`);
  };

  // Arrow keys move the crosshair (Shift for bigger steps), Enter or Space sets an
  // end at it, and Escape clears the line without closing the window.
  const onPickKey = (e) => {
    const step = (e.shiftKey ? 0.05 : 0.01) * Math.max(w, h);
    const c = cursor || [w / 2, h / 2];
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      e.preventDefault();
      setCursor([Math.max(0, Math.min(w, c[0] + moves[e.key][0])), Math.max(0, Math.min(h, c[1] + moves[e.key][1]))]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setPoint(c);
    } else if (e.key === 'Escape' && points.length) {
      e.preventDefault();
      e.stopPropagation();
      setPoints([]);
      setPickMsg('Line cleared.');
    }
  };

  // Resolves to the first place found, or null. Shared by the Search button,
  // leaving the field, and Save — a typed place never has to be searched by hand,
  // and a click on Search while leaving the field starts one lookup, not two.
  const search = () => {
    const q = query.trim();
    if (!q) return Promise.resolve(null);
    if (searchRef.current && searchRef.current.q === q) return searchRef.current.run;
    setSearching(true);
    setPlaceMsg('');
    const run = (async () => {
      let found = null;
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(q)}`);
        const d = await r.json();
        if (!Array.isArray(d) || !d.length) {
          setResults([]);
          setPlaceMsg('No place found for that search. Try a nearby landmark, area or city.');
        } else {
          const list = d.map((x) => ({ lat: Number(x.lat), lng: Number(x.lon), label: x.display_name }));
          setResults(list);
          setPlace(list[0]);
          found = list[0];
        }
      } catch {
        setPlaceMsg('The place search could not be reached.');
      } finally {
        setSearching(false);
        if (!found && searchRef.current && searchRef.current.q === q) searchRef.current = null;   // let a retry run
      }
      return found;
    })();
    searchRef.current = { q, run };
    return run;
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setPlaceMsg('This browser cannot share its location.'); return; }
    setSearching(true);
    setPlaceMsg('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setResults([]);
        setPlace({ lat: latitude, lng: longitude, fromDevice: true, label: `Current location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})` });
        setSearching(false);
      },
      () => { setPlaceMsg('Location was not shared. Search for the place instead.'); setSearching(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const missing = !name.trim() ? 'Name the project.'
    : !place && !query.trim() ? 'Set the location — search for a place, or use your location.'
      : northDeg == null ? 'Say which way north points on the drawing.'
        : method === 'line' && points.length < 2 ? 'Mark both ends of a line you know the length of.'
          : !mpp ? (method === 'line' ? 'Enter that line’s real length.' : 'Enter the outline’s real overall width.')
            : '';
  const ready = !missing;

  const save = async () => {
    if (!ready || saving) return;
    setError('');
    try {
      let at = place;
      if (!at) {
        setSaving('Finding the location…');
        at = await search();
        if (!at) throw new Error(`“${query.trim()}” could not be found. Try a nearby landmark, area or city, or use your location.`);
      }
      const site = siteFromDrawing({ outline, mpp, northDeg, lat: at.lat, lng: at.lng });
      if (!site) throw new Error('A site boundary could not be built from this outline.');

      setSaving('Creating the project…');
      const saved = await onCreateProject({
        name: name.trim(),
        polygonLatLng: site.polygon,
        areaM2: site.areaM2,
        perimeterM: site.perimeterM,
        location: at.label,
        extra: {
          siteSource: 'drawing',
          drawing: {
            refId: draft.refId,
            fileName: draft.name,
            source: kind,
            mpp,
            northDeg,
            scaleFrom: method === 'width'
              ? { method: 'width', widthM, unit, widthPx: bounds.widthPx }
              : { method: 'line', a: points[0], b: points[1], lengthM, unit },
            imageSize: draft.parse.imageSize,
            // the boundary as saved, in drawing pixels — the traced one, or as corrected
            outlinePx: outline,
            outlineEdited: edited,
            centroidPx: site.centroidPx,
            vertexMeanPx: site.vertexMeanPx,
          },
          designReferences: {
            floorPlans: [{ floorId: '0', label: floorLabel(0), fileName: draft.name, size: draft.size, refId: draft.refId }],
            elevations: [],
            model3d: null,
          },
        },
      });

      setSaving('Linking the drawing…');
      await readJson(fetch(`${apiBase}/api/design-reference/${draft.refId}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: saved.id, kind: 'floorPlan', slot: '0' }),
      }), 'Linking the drawing');

      setPreferredPlan(saved.id, draft.refId);
      onSaved(saved);
    } catch (e) {
      setError(e.message || 'The project could not be saved.');
      setSaving('');
    }
  };

  const r = Math.max(w, h) / 150;
  const hr = Math.max(w, h) / 120;       // corner handle radius, in drawing pixels

  return (
    <dialog
      ref={dialogRef}
      className="intake-dialog"
      aria-labelledby="intake-save-title"
      onCancel={(e) => { e.preventDefault(); if (!saving) onCancel(); }}
    >
      <div className="intake-dialog-head">
        <h2 id="intake-save-title" className="intake-title">Save Project</h2>
        <button type="button" className="btn" onClick={onCancel} disabled={!!saving}>Cancel</button>
      </div>

      <div className="intake-dialog-body">
        <div className="intake-dialog-grid">
          <div>
            <p className="intake-step">
              {method === 'line'
                ? <><b>Dimension.</b> Click both ends of one line whose real length you know — a plot edge or a wall. Or Tab to the drawing and use the arrow keys, then Enter.</>
                : <><b>Dimension.</b> The red line spans the traced outline, left to right. Enter how wide that really is.</>}
            </p>
            <div
              className="intake-pick"
              tabIndex={method === 'line' ? 0 : -1}
              role="application"
              aria-label="The drawing. Arrow keys move a crosshair, with Shift for bigger steps. Enter sets an end of the line. Escape clears it."
              onKeyDown={method === 'line' ? onPickKey : undefined}
              onFocus={() => { if (method === 'line' && !cursor) setCursor([w / 2, h / 2]); }}
              onBlur={() => setCursor(null)}
            >
              <img src={draft.rasterUrl} alt={`The drawing: ${draft.name}`} draggable={false} />
              <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" onClick={method === 'line' ? pick : undefined}
                   onPointerDown={() => { handledRef.current = false; }}
                   onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}>
                <polygon className="intake-outline" points={outline.map((p) => p.join(',')).join(' ')} />
                {outline.map((a, i) => {
                  const b = outline[(i + 1) % outline.length];
                  // no midpoint on an edge too short to hold one without crowding its corners
                  if (Math.hypot(b[0] - a[0], b[1] - a[1]) < hr * 7) return null;
                  const mx = (a[0] + b[0]) / 2;
                  const my = (a[1] + b[1]) / 2;
                  return (
                    <g key={`m${i}`} className="intake-mid" onPointerDown={(e) => onHandleDown(e, i, true)}>
                      <title>Drag to add a corner here</title>
                      <circle className="intake-hit" cx={mx} cy={my} r={hr * 1.4} />
                      <circle className="intake-mid-dot" cx={mx} cy={my} r={hr * 0.55} />
                    </g>
                  );
                })}
                {outline.map((q, i) => (
                  <g
                    key={`v${i}`}
                    ref={(el) => { vertexRefs.current[i] = el; }}
                    className="intake-vertex"
                    tabIndex={i === Math.min(focusIdx, outline.length - 1) ? 0 : -1}
                    role="button"
                    aria-label={`Outline corner ${i + 1} of ${outline.length}, ${where(q)}. Arrow keys move it, Page Down goes to the next corner, Insert adds one, Delete removes it, Enter marks it as a line end.`}
                    onPointerDown={(e) => onHandleDown(e, i, false)}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); dragRef.current = null; removeVertex(i); }}
                    onKeyDown={(e) => onHandleKey(e, i)}
                    onFocus={() => setFocusIdx(i)}
                  >
                    <circle className="intake-hit" cx={q[0]} cy={q[1]} r={hr * 1.8} />
                    <circle className="intake-vertex-dot" cx={q[0]} cy={q[1]} r={hr} />
                  </g>
                ))}
                {method === 'line' && points.length === 2 && (
                  <line className="intake-line" x1={points[0][0]} y1={points[0][1]} x2={points[1][0]} y2={points[1][1]} />
                )}
                {method === 'line' && points.map((p, i) => <circle key={i} className="intake-dot" cx={p[0]} cy={p[1]} r={r} />)}
                {method === 'width' && (
                  <line className="intake-line" x1={bounds.minX} y1={bounds.maxY} x2={bounds.maxX} y2={bounds.maxY} />
                )}
                {method === 'line' && cursor && (
                  <g className="intake-cross">
                    <line x1={cursor[0]} y1={0} x2={cursor[0]} y2={h} />
                    <line x1={0} y1={cursor[1]} x2={w} y2={cursor[1]} />
                  </g>
                )}
              </svg>
              {northDeg != null && <NorthBadge deg={northDeg} />}
            </div>
            <p className="intake-note">
              The dashed line is the traced outline — it becomes the site boundary. Drag a corner to correct it,
              drag a small midpoint to add a corner, and right-click (or long-press) a corner to remove it.
              {method === 'line' && ' Clicking a corner marks a line end exactly on it.'}
              {method === 'line' && points.length === 1 && ' Now mark the other end.'}
              {method === 'line' && points.length === 2 && ' Mark again to start a different line.'}
            </p>
            {edited && (
              <p className="intake-note">
                Outline corrected by hand — {outline.length} corners.{' '}
                <button type="button" className="btn intake-reset" onClick={resetOutline}>Reset to traced</button>
              </p>
            )}
            <p className="intake-sr" aria-live="polite">{pickMsg}</p>
          </div>

          <div>
            <label className="intake-fieldset">
              <span className="intake-legend">Project name</span>
              <input className="intake-input" type="text" value={name} placeholder="Enter project name"
                     onChange={(e) => setName(e.target.value)} />
            </label>

            <fieldset className="intake-fieldset">
              <legend className="intake-legend">Location</legend>
              <div className="intake-row">
                <input className="intake-input" type="search" value={query} placeholder="Search a place, area or address"
                       onChange={(e) => {
                         setQuery(e.target.value);
                         // a place found for the old text no longer answers the new text
                         if (place && !place.fromDevice) { setPlace(null); setResults([]); }
                       }}
                       onBlur={() => { if (query.trim() && !place) search(); }}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }} />
                <button type="button" className="btn" onClick={search} disabled={searching || !query.trim()}>Search</button>
                <button type="button" className="btn" onClick={useMyLocation} disabled={searching}>Use my location</button>
              </div>
              {results.length > 1 && (
                <select className="intake-input" aria-label="Search results"
                        value={results.findIndex((x) => place && x.lat === place.lat && x.lng === place.lng)}
                        onChange={(e) => setPlace(results[Number(e.target.value)])}>
                  {results.map((x, i) => <option key={`${x.lat},${x.lng}`} value={i}>{x.label}</option>)}
                </select>
              )}
              {place && <p className="intake-place">{place.label}</p>}
              {placeMsg && <p className="intake-note intake-note--err">{placeMsg}</p>}
            </fieldset>

            <fieldset className="intake-fieldset">
              <legend className="intake-legend">North</legend>
              <div className="intake-quick" role="group" aria-label="North points to">
                {QUICK_NORTH.map((q) => (
                  <button key={q.deg} type="button" className="btn intake-chip"
                          aria-pressed={northDeg === q.deg} onClick={() => setNorth(String(q.deg))}>
                    {q.label}
                  </button>
                ))}
              </div>
              <div className="intake-row">
                <input className="intake-input intake-input--short" type="number" min="0" max="359" step="1"
                       value={north} placeholder="degrees" aria-label="North, in degrees clockwise from the top of the drawing"
                       onChange={(e) => setNorth(e.target.value)} />
                <span className="intake-note">° clockwise from the top of the drawing</span>
              </div>
            </fieldset>

            <fieldset className="intake-fieldset">
              <legend className="intake-legend">Scale</legend>
              <div className="intake-quick" role="group" aria-label="Set the scale by">
                <button type="button" className="btn intake-chip" aria-pressed={method === 'line'}
                        onClick={() => setMethod('line')}>Mark a line</button>
                <button type="button" className="btn intake-chip" aria-pressed={method === 'width'}
                        onClick={() => setMethod('width')}>Overall width</button>
              </div>
              <div className="intake-row">
                {method === 'line' ? (
                  <input className="intake-input intake-input--short" type="number" min="0" step="0.01"
                         value={length} placeholder="length" aria-label="Real length of the marked line"
                         onChange={(e) => setLength(e.target.value)} />
                ) : (
                  <input className="intake-input intake-input--short" type="number" min="0" step="0.01"
                         value={widthLen} placeholder="width" aria-label="Real overall width of the traced outline, left to right"
                         onChange={(e) => setWidthLen(e.target.value)} />
                )}
                <select className="intake-input intake-input--unit" value={unit} aria-label="Unit"
                        onChange={(e) => setUnit(e.target.value)}>
                  <option value="m">metres</option>
                  <option value="ft">feet</option>
                </select>
              </div>
              {mpp && <p className="intake-note">Scale: 1 px = {mpp.toFixed(4)} m</p>}
            </fieldset>

            {preview && (
              <p className="intake-preview-card">
                Site from the traced outline: <strong>{fmt(preview.areaM2, 0)} m²</strong>
                {' '}({fmt(preview.widthM)} × {fmt(preview.depthM)} m). If that looks wrong, check the
                line and its length.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="intake-dialog-foot">
        <p className={`intake-status${error ? ' is-error' : ''}`} role="status" aria-live="polite">
          {error || saving || missing || (place ? 'Ready to save.' : `Ready — Save will look up “${query.trim()}”.`)}
        </p>
        <button type="button" className="btn primary intake-primary" disabled={!ready || !!saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </dialog>
  );
}

/* ---- the panel ----------------------------------------------------------------- */
export default function DrawingIntake({ mode, apiBase, onCancel, onCreateProject, onSaved }) {
  const copy = COPY[mode] || COPY.photo;
  const sketchRef = useRef(null);
  const pickRef = useRef(null);
  const cameraRef = useRef(null);

  const [file, setFile] = useState(null);        // { blob, name, size, previewUrl }
  const [hasInk, setHasInk] = useState(false);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);      // { refId, name, size, parse, rasterUrl }

  useEffect(() => () => { if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl); }, [file]);

  const accept = async (picked) => {
    setError('');
    if (!picked) return;
    if (mode === 'pdf') {
      const isPdf = picked.type === 'application/pdf' || /\.pdf$/i.test(picked.name || '');
      if (!isPdf) { setError('That file is not a PDF.'); return; }
      if (picked.size > MAX_MB * 1048576) { setError(`That PDF is ${mb(picked.size)}; the limit is ${MAX_MB} MB.`); return; }
      setFile({ blob: picked, name: picked.name, size: picked.size, previewUrl: null });
      return;
    }
    let blob = picked;
    let name = picked.name || `photo-${stamp()}.jpg`;
    const native = picked.type === 'image/jpeg' || picked.type === 'image/png';
    if (!native || picked.size > MAX_MB * 1048576) {
      try {
        blob = await toJpeg(picked);
        name = `${(picked.name || `photo-${stamp()}`).replace(/\.[^.]+$/, '')}.jpg`;
      } catch {
        setError('This image could not be read in this browser. Save it as JPG or PNG and try again.');
        return;
      }
      if (blob.size > MAX_MB * 1048576) { setError(`Even after resizing, the photo is ${mb(blob.size)}; the limit is ${MAX_MB} MB.`); return; }
    }
    setFile({ blob, name, size: blob.size, previewUrl: URL.createObjectURL(blob) });
  };

  const onPick = (e) => {
    accept(e.target.files && e.target.files[0]);
    e.target.value = '';
  };

  const ready = mode === 'sketch' ? hasInk : !!file;

  // Upload and trace BEFORE the Save window: a PDF has to be rendered, and the
  // outline found, before anyone can mark a line on it. The store is content-
  // addressed, so pressing this again for the same drawing re-uses the upload.
  const letsDesign = async () => {
    if (!ready || busy) return;
    setError('');
    try {
      let blob;
      let name;
      if (mode === 'sketch') {
        blob = await sketchRef.current.exportPng();
        name = `sketch-${stamp()}.png`;
      } else {
        blob = file.blob;
        name = file.name;
      }
      setBusy('Uploading the drawing…');
      const form = new FormData();
      form.append('file', blob, name);
      form.append('kind', 'floorPlan');
      form.append('slot', '0');
      // Pending until the saved project claims it; a cancelled Save discards it.
      form.append('pending', '1');
      const u = await readJson(fetch(`${apiBase}/api/design-reference`, { method: 'POST', body: form }), 'The upload');

      setBusy('Tracing its outline…');
      const p = await readJson(fetch(`${apiBase}/api/design-reference/${u.refId}/parse`, { method: 'POST' }), 'Tracing the drawing');
      if (!Array.isArray(p.outline) || p.outline.length < 3 || !p.imageSize?.w || !p.imageSize?.h) {
        throw new Error('No closed outline could be traced from this drawing, so there is no boundary to build a site from. A drawing whose outer boundary is one closed line works best.');
      }
      // A sliver — one stroke traced instead of a boundary — would save as a site a
      // metre deep. Stop here, where the drawing can still be fixed.
      if (pixelArea(p.outline) < 0.01 * p.imageSize.w * p.imageSize.h) {
        throw new Error('Only a single line was traced from this drawing, not a closed boundary, so it cannot become a site. Join the corners of the plot so its outline is one closed shape, then press Let’s Design again.');
      }
      setDraft({ refId: u.refId, name, size: blob.size, parse: p, rasterUrl: `${apiBase}/api/design-reference/${u.refId}/raster` });
    } catch (e) {
      setError(e.message || 'The drawing could not be prepared.');
    } finally {
      setBusy('');
    }
  };

  // A drawing uploaded for a Save window that was then cancelled is not kept. The
  // server only discards PENDING uploads, so this can never remove a saved one.
  const discard = (refId) => {
    if (!refId) return;
    fetch(`${apiBase}/api/design-reference/${refId}/pending`, { method: 'DELETE' }).catch(() => {});
  };
  const discardDraft = () => { discard(draft && draft.refId); setDraft(null); };
  // Leaving the panel with the window still open discards it too — unless it was
  // just saved, which is what unmounts the panel on success.
  const draftRef = useRef(null);
  const savedRef = useRef(false);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => () => {
    if (draftRef.current && !savedRef.current) discard(draftRef.current.refId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const status = error || busy
    || (mode === 'sketch'
      ? (hasInk ? 'Ready — press Let’s Design.' : 'Draw something to begin.')
      : (file ? 'Ready — press Let’s Design.' : (mode === 'pdf' ? 'Choose a PDF to begin.' : 'Choose or take a photo to begin.')));

  return (
    <section className="intake" aria-labelledby="intake-title">
      <header className="intake-head">
        <div>
          <h2 id="intake-title" className="intake-title">{copy.title}</h2>
          <p className="intake-sub">{copy.sub}</p>
        </div>
        <button type="button" className="btn" onClick={onCancel} disabled={!!busy}>Back</button>
      </header>

      <div className="intake-body">
        {mode === 'sketch' && (
          <div className="intake-sketch">
            <SketchCanvas ref={sketchRef} onInkChange={setHasInk} />
          </div>
        )}

        {mode !== 'sketch' && !file && (
          <div
            className={`intake-drop${over ? ' is-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); accept(e.dataTransfer.files && e.dataTransfer.files[0]); }}
          >
            <div className="intake-drop-actions">
              <button type="button" className="btn" onClick={() => pickRef.current && pickRef.current.click()}>
                {mode === 'pdf' ? 'Choose a PDF' : 'Choose a photo'}
              </button>
              {mode === 'photo' && (
                <button type="button" className="btn" onClick={() => cameraRef.current && cameraRef.current.click()}>
                  Take a photo
                </button>
              )}
            </div>
            <p className="intake-hint">
              {mode === 'pdf'
                ? `or drop a PDF here — up to ${MAX_MB} MB`
                : `or drop an image here — JPG or PNG up to ${MAX_MB} MB; other formats are converted. On a phone or tablet, Take a photo opens the camera.`}
            </p>
          </div>
        )}

        {mode === 'photo' && file && (
          <div className="intake-preview">
            <div className="intake-preview-frame"><img src={file.previewUrl} alt={`Preview of ${file.name}`} /></div>
            <div className="intake-file">
              <span><strong>{file.name}</strong> · {mb(file.size)}</span>
              <button type="button" className="btn" onClick={() => setFile(null)} disabled={!!busy}>Choose another</button>
            </div>
          </div>
        )}

        {mode === 'pdf' && file && (
          <div className="intake-drop">
            <div className="intake-file">
              <span><strong>{file.name}</strong> · {mb(file.size)}</span>
              <button type="button" className="btn" onClick={() => setFile(null)} disabled={!!busy}>Choose another</button>
            </div>
          </div>
        )}

        <input ref={pickRef} type="file" hidden onChange={onPick}
               accept={mode === 'pdf' ? 'application/pdf,.pdf' : 'image/jpeg,image/png,image/*'} />
        {mode === 'photo' && (
          <input ref={cameraRef} type="file" hidden accept="image/*" capture="environment" onChange={onPick} />
        )}
      </div>

      <footer className="intake-foot">
        <p className={`intake-status${error ? ' is-error' : ''}`} role="status" aria-live="polite">{status}</p>
        <button type="button" className="btn primary intake-primary" disabled={!ready || !!busy} onClick={letsDesign}>
          {busy ? 'Preparing…' : 'Let’s Design'}
        </button>
      </footer>

      {draft && (
        <SaveProjectDialog
          draft={draft}
          kind={mode}
          apiBase={apiBase}
          onCancel={discardDraft}
          onCreateProject={onCreateProject}
          onSaved={(saved) => { savedRef.current = true; onSaved(saved); }}
        />
      )}
    </section>
  );
}
